<template>
  <div>
    <CCard>
      <CCardBody>
        <div class="d-flex flex-wrap align-items-start justify-content-between" style="gap: 0.75rem;">
          <div>
            <h4 class="card-title mb-0">Invoice Report</h4>
          </div>

          <CButton
            size="sm"
            color="primary"
            variant="outline"
            :disabled="loading"
            @click="getInvoiceReport"
          >
            <CSpinner v-if="loading" size="sm" />
            <template v-else>
              <CIcon name="cil-reload" /> Refresh
            </template>
          </CButton>
        </div>

        <section class="filters-panel my-3">
          <div class="filters-grid">
            <div class="form-group mb-0">
              <label>Period</label>
              <select class="custom-select" v-model="filters.period" @change="onPeriodChange">
                <option v-for="option in periodOptions" :key="option.value" :value="option.value">
                  {{ option.label }}
                </option>
              </select>
            </div>

            <CInput
              class="mb-0"
              type="date"
              label="From"
              v-model="filters.date_from"
              @change="onDateChange"
            />

            <CInput
              class="mb-0"
              type="date"
              label="To"
              v-model="filters.date_to"
              @change="onDateChange"
            />

            <div class="form-group mb-0">
              <label>Transferred</label>
              <select class="custom-select" v-model="filters.transfered" @change="getInvoiceReport">
                <option v-for="option in transferOptions" :key="option.value" :value="option.value">
                  {{ option.label }}
                </option>
              </select>
            </div>

            <div class="form-group mb-0">
              <label>Invoice type</label>
              <select class="custom-select" v-model="filters.invoice_type">
                <option v-for="option in invoiceTypeOptions" :key="option.value" :value="option.value">
                  {{ option.label }}
                </option>
              </select>
            </div>

            <div class="form-group mb-0">
              <label>Billing entity</label>
              <select
                class="custom-select"
                v-model="filters.billing_entity_id"
                :disabled="billingEntitiesLoading"
                @change="getInvoiceReport"
              >
                <option :value="null">All entities (combined)</option>
                <option v-for="entity in countryBillingEntities" :key="entity.id" :value="entity.id">
                  {{ entity.code }} — {{ entity.legal_name }} ({{ entity.status }})
                </option>
              </select>
            </div>
          </div>
        </section>

        <div v-if="loading && !invoiceReport" class="py-3">
          <CSpinner color="primary" />
        </div>

        <div v-else class="table-responsive">
          <table class="table table-sm table-striped invoice-report-table mb-0">
            <thead>
              <tr>
                <th>Type</th>
                <th class="text-right">Invoices</th>
                <th class="text-right">Orders</th>
                <th class="text-right">Total incl.</th>
                <th class="text-right">Total excl.</th>
                <th class="text-right">VAT</th>
                <th class="text-right">Volume</th>
                <th class="text-right">DS volume</th>
                <th class="text-right">Shipping</th>
                <th class="text-right">DS shipping</th>
                <th class="text-right">ivCommC</th>
                <th class="text-right">ivCommC_ds</th>
                <th class="text-right">ivDataCost</th>
                <th class="text-right">Administration costs</th>
                <th class="text-right">Merchandise</th>
                <th class="text-right">Credit notes</th>
                <th class="text-right">Pay to cust.</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in reportRows" :key="row.key">
                <th>{{ row.label }}</th>
                <td class="text-right">{{ row.invoice_count }}</td>
                <td class="text-right">{{ row.orders_count }}</td>
                <td class="text-right">{{ row.ivTotIncl | toCurrency('remove_thousand_separator') }}</td>
                <td class="text-right">{{ row.ivTotExcl | toCurrency('remove_thousand_separator') }}</td>
                <td class="text-right">{{ row.ivBtwC | toCurrency('remove_thousand_separator') }}</td>
                <td class="text-right">{{ row.ivTotVol | toCurrency('remove_thousand_separator') }}</td>
                <td class="text-right">{{ row.ivTotVol_ds | toCurrency('remove_thousand_separator') }}</td>
                <td class="text-right">{{ row.shipping_cost | toCurrency('remove_thousand_separator') }}</td>
                <td class="text-right">{{ row.ds_shipping_cost | toCurrency('remove_thousand_separator') }}</td>
                <td class="text-right">{{ row.ivCommC | toCurrency('remove_thousand_separator') }}</td>
                <td class="text-right">{{ row.ivCommC_ds | toCurrency('remove_thousand_separator') }}</td>
                <td class="text-right">{{ row.ivDataCost | toCurrency('remove_thousand_separator') }}</td>
                <td class="text-right">{{ row.administration_costs | toCurrency('remove_thousand_separator') }}</td>
                <td class="text-right">{{ row.merchandise_total_amount | toCurrency('remove_thousand_separator') }}</td>
                <td class="text-right">{{ row.creditnote_total_amount | toCurrency('remove_thousand_separator') }}</td>
                <td class="text-right">{{ row.ivPymntToCust | toCurrency('remove_thousand_separator') }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
export default {
  name: 'InvoiceReport',
  data() {
    return {
      loading: false,
      billingEntitiesLoading: false,
      billingEntities: [],
      invoiceReport: null,
      filters: {
        period: 'this_month',
        date_from: null,
        date_to: null,
        transfered: 'all',
        invoice_type: 'all',
        billing_entity_id: null,
      },
      periodOptions: [
        { value: 'today', label: 'Today' },
        { value: 'yesterday', label: 'Yesterday' },
        { value: 'this_week', label: 'This week' },
        { value: 'last_week', label: 'Last week' },
        { value: 'this_month', label: 'This month' },
        { value: 'last_month', label: 'Last month' },
        { value: 'this_quarter', label: 'This quarter' },
        { value: 'last_quarter', label: 'Last quarter' },
        { value: 'this_year', label: 'This year' },
        { value: 'last_year', label: 'Last year' },
        { value: 'custom', label: 'Custom' },
      ],
      transferOptions: [
        { value: 'all', label: 'All' },
        { value: 'transfered', label: 'Transferred' },
        { value: 'not-transfered', label: 'Not transferred' },
      ],
      invoiceTypeOptions: [
        { value: 'all', label: 'Alle facturen' },
        { value: 'restaurant', label: 'Restaurant invoice' },
        { value: 'menutepay', label: 'MenutePay invoice' },
      ],
    };
  },

  computed: {
    selectedCountryCode() {
      const selectedCountry = this.authCountries.find(country => country.value === this.$store.state.countryId);

      return selectedCountry ? selectedCountry.iso2.toUpperCase() : null;
    },

    countryBillingEntities() {
      return this.billingEntities.filter(entity => (
        !this.selectedCountryCode || entity.country_code === this.selectedCountryCode
      ));
    },

    reportRows() {
      const totals = this.invoiceReport?.totals || {};
      const rows = [
        { key: 'restaurant', label: 'Restaurant invoices', ...(totals.restaurant || {}) },
        { key: 'menutepay', label: 'MenutePay invoices', ...(totals.menutepay || {}) },
        { key: 'all', label: 'Totaal', ...(totals.all || {}) },
      ];

      return rows
        .filter(row => this.filters.invoice_type === 'all' || row.key === this.filters.invoice_type)
        .map(row => ({
          invoice_count: 0,
          orders_count: 0,
          ivTotIncl: 0,
          ivTotExcl: 0,
          ivBtwC: 0,
          ivTotVol: 0,
          ivTotVol_ds: 0,
          shipping_cost: 0,
          ds_shipping_cost: 0,
          ivCommC: 0,
          ivCommC_ds: 0,
          ivDataCost: 0,
          administration_costs: 0,
          merchandise_total_amount: 0,
          creditnote_total_amount: 0,
          ivPymntToCust: 0,
          ...row,
        }));
    },
  },

  async mounted() {
    this.setPeriodDates();
    await this.loadBillingEntities();
    await this.getInvoiceReport();
  },

  watch: {
    '$store.state.countryId'() {
      this.selectDefaultBillingEntity();
      this.getInvoiceReport();
    },
  },

  methods: {
    async loadBillingEntities() {
      this.billingEntitiesLoading = true;

      try {
        const { data } = await this.$axios.get(this.$backend.BILLING_ENTITIES.GET_ALL);
        this.billingEntities = data;
        this.selectDefaultBillingEntity();
      } finally {
        this.billingEntitiesLoading = false;
      }
    },

    selectDefaultBillingEntity() {
      const activeEntity = this.countryBillingEntities.find(entity => (
        entity.status === 'active' && entity.is_enabled
      ));

      this.filters.billing_entity_id = activeEntity ? activeEntity.id : null;
    },

    onPeriodChange() {
      if (this.filters.period !== 'custom') {
        this.setPeriodDates();
      }

      this.getInvoiceReport();
    },

    onDateChange() {
      this.filters.period = 'custom';
      this.getInvoiceReport();
    },

    async getInvoiceReport() {
      this.loading = true;

      const url = new URL(this.$backend.REPORTS.INVOICES);
      const params = {
        period: this.filters.period,
        date_from: this.filters.period === 'custom' ? this.filters.date_from : null,
        date_to: this.filters.period === 'custom' ? this.filters.date_to : null,
        transfered: this.filters.transfered,
        billing_entity_id: this.filters.billing_entity_id,
        billing_entity_country_code: this.selectedCountryCode,
      };

      Object.keys(params).forEach((key) => {
        if (
          typeof params[key] !== 'undefined' &&
          params[key] !== null &&
          params[key] !== '' &&
          params[key].length !== 0
        ) {
          url.searchParams.append(key, params[key]);
        }
      });

      try {
        const { data } = await this.$axios.get(url);
        this.invoiceReport = data;
        this.filters.period = data.period;
        this.filters.date_from = data.date_from;
        this.filters.date_to = data.date_to;
        this.filters.transfered = data.transfered;
        this.filters.billing_entity_id = data.billing_entity_id;
      } finally {
        this.loading = false;
      }
    },

    setPeriodDates() {
      const now = new Date();
      let startDate;
      let endDate;

      switch (this.filters.period) {
        case 'today':
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          break;

        case 'yesterday':
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
          endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
          break;

        case 'this_week':
          startDate = this.getMonday(now);
          endDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + 6);
          break;

        case 'last_week': {
          const lastWeekDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
          startDate = this.getMonday(lastWeekDate);
          endDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + 6);
          break;
        }

        case 'last_month':
          startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          endDate = new Date(now.getFullYear(), now.getMonth(), 0);
          break;

        case 'this_quarter': {
          const quarterStartMonth = Math.floor(now.getMonth() / 3) * 3;
          startDate = new Date(now.getFullYear(), quarterStartMonth, 1);
          endDate = new Date(now.getFullYear(), quarterStartMonth + 3, 0);
          break;
        }

        case 'last_quarter': {
          const quarterStartMonth = Math.floor(now.getMonth() / 3) * 3 - 3;
          startDate = new Date(now.getFullYear(), quarterStartMonth, 1);
          endDate = new Date(now.getFullYear(), quarterStartMonth + 3, 0);
          break;
        }

        case 'this_year':
          startDate = new Date(now.getFullYear(), 0, 1);
          endDate = new Date(now.getFullYear(), 11, 31);
          break;

        case 'last_year':
          startDate = new Date(now.getFullYear() - 1, 0, 1);
          endDate = new Date(now.getFullYear() - 1, 11, 31);
          break;

        case 'this_month':
        default:
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
          break;
      }

      this.filters.date_from = this.formatDateInput(startDate);
      this.filters.date_to = this.formatDateInput(endDate);
    },

    getMonday(date) {
      const result = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const day = result.getDay();
      const diff = result.getDate() - day + (day === 0 ? -6 : 1);

      return new Date(result.setDate(diff));
    },

    formatDateInput(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');

      return `${year}-${month}-${day}`;
    },
  },
};
</script>

<style scoped>
.filters-panel {
  border: 1px solid #d8dbe0;
  border-left: 4px solid #f9b115;
  border-radius: 4px;
  padding: 0.75rem;
  background: #fff;
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(140px, 1fr));
  gap: 0.75rem;
  align-items: end;
}

.invoice-report-table {
  min-width: 1420px;
}

@media (max-width: 1199.98px) {
  .filters-grid {
    grid-template-columns: repeat(3, minmax(140px, 1fr));
  }
}

@media (max-width: 767.98px) {
  .filters-grid {
    grid-template-columns: 1fr;
  }
}
</style>
